import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogIn } from "lucide-react";
import { useAuth } from "@/contexts/useAuth";

const Navbar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAccessPortal = !!user && (user.isAdmin || user.isProvisionedClient);

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/#services", label: "Services" },
    { to: "/#process", label: "Process" },
    { to: "/#pricing", label: "Pricing" },
    { to: "/#faq", label: "FAQ" },
  ];

  const handleBookCall = () => {
    if (canAccessPortal) {
      navigate("/portal");
    } else {
      navigate("/contact");
    }
  };

  const handlePortal = () => {
    if (canAccessPortal) {
      navigate("/portal");
    } else {
      navigate("/auth");
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 flex justify-center py-4 px-4">
        <nav
          className={`flex items-center justify-between w-full max-w-7xl transition-all duration-500 ${
            scrolled
              ? "glass-panel rounded-2xl px-6 py-3 shadow-soft"
              : "px-2 py-2"
          }`}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-heading font-semibold text-lg tracking-tight">5AM Founders</span>
          </Link>

          {/* Center Nav - Desktop */}
          <div className="hidden md:flex items-center glass-panel rounded-full px-2 py-1.5 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="relative text-muted-foreground hover:text-foreground px-4 py-2 text-sm font-medium transition-all duration-300 rounded-full hover:bg-muted/50"
              >
                {link.label}
              </Link>
            ))}
            {canAccessPortal ? (
              <Button variant="cta" size="sm" className="ml-2" onClick={handlePortal}>
                Portal
              </Button>
            ) : (
              <>
                <Button variant="cta" size="sm" className="ml-2" onClick={handleBookCall}>
                  Book a Call
                </Button>
                <Button variant="ghost" size="sm" onClick={handlePortal}>
                  <LogIn className="w-4 h-4 mr-2" />
                  Portal
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`fixed inset-x-4 top-20 md:hidden transition-all duration-300 ${
            mobileOpen
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-4 pointer-events-none"
          }`}
        >
          <div className="glass-panel rounded-2xl p-6 shadow-glow">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-foreground hover:text-primary px-4 py-3 text-base font-medium transition-colors rounded-xl hover:bg-muted/50"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {canAccessPortal ? (
                <Button
                  variant="cta"
                  size="lg"
                  className="mt-4"
                  onClick={() => {
                    setMobileOpen(false);
                    handlePortal();
                  }}
                >
                  Portal
                </Button>
              ) : (
                <>
                  <Button
                    variant="cta"
                    size="lg"
                    className="mt-4"
                    onClick={() => {
                      setMobileOpen(false);
                      handleBookCall();
                    }}
                  >
                    Book a Call
                  </Button>
                  <Button
                    variant="glass"
                    size="lg"
                    onClick={() => {
                      setMobileOpen(false);
                      handlePortal();
                    }}
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Portal Login
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
